import { useCustomMutation } from '@refinedev/core';
import { Modal, Input, Select, Switch, message } from 'antd';
import { useState, useEffect, type FC } from 'react';

interface AiProviderConfig {
  taskType: string;
  provider: string;
  model: string;
  fallbackProvider: string | null;
  fallbackModel: string | null;
  active: boolean;
}

interface AiProviderEditModalProps {
  record: AiProviderConfig | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'ollama', label: 'Ollama (local)' },
  { value: 'groq', label: 'Groq' },
];

const AiProviderEditModal: FC<AiProviderEditModalProps> = ({ record, onClose, onSuccess }) => {
  const { mutate, isLoading } = useCustomMutation();

  const [form, setForm] = useState({
    provider: '',
    model: '',
    fallbackProvider: '',
    fallbackModel: '',
    active: true,
  });

  useEffect(() => {
    if (record) {
      setForm({
        provider: record.provider,
        model: record.model,
        fallbackProvider: record.fallbackProvider ?? '',
        fallbackModel: record.fallbackModel ?? '',
        active: record.active,
      });
    }
  }, [record]);

  const handleSubmit = () => {
    if (!record) return;

    mutate(
      {
        url: `/api/v1/admin/ai/providers/${record.taskType}`,
        method: 'put',
        values: {
          provider: form.provider,
          model: form.model,
          fallbackProvider: form.fallbackProvider || null,
          fallbackModel: form.fallbackModel || null,
          active: form.active,
        },
      },
      {
        onSuccess: () => {
          void message.success('Configuration mise a jour');
          onSuccess();
        },
        onError: () => {
          void message.error('Erreur lors de la mise a jour');
        },
      },
    );
  };

  return (
    <Modal
      title={`Modifier : ${record?.taskType ?? ''}`}
      open={record !== null}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      okText="Enregistrer"
      cancelText="Annuler"
      destroyOnClose
    >
      <div className="space-y-4 pt-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Provider principal
          </label>
          <Select
            className="w-full"
            value={form.provider}
            onChange={(v) => setForm((f) => ({ ...f, provider: v }))}
            options={PROVIDER_OPTIONS}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Modele
          </label>
          <Input
            value={form.model}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            placeholder="Ex: gpt-4o, claude-sonnet-4-20250514"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Provider fallback
          </label>
          <Select
            className="w-full"
            allowClear
            value={form.fallbackProvider || undefined}
            onChange={(v) => setForm((f) => ({ ...f, fallbackProvider: v ?? '' }))}
            options={PROVIDER_OPTIONS}
            placeholder="Aucun"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Modele fallback
          </label>
          <Input
            value={form.fallbackModel}
            onChange={(e) => setForm((f) => ({ ...f, fallbackModel: e.target.value }))}
            placeholder="Ex: claude-sonnet-4-20250514"
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={form.active}
            onChange={(v) => setForm((f) => ({ ...f, active: v }))}
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            {form.active ? 'Actif' : 'Inactif'}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default AiProviderEditModal;
